import React, { Fragment, useEffect, useState } from 'react';
import AdminPageLoader from './AdminPageLoader';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaEdit, FaRegEdit, FaPlus, FaSearch, FaImage, FaSpinner, FaFolder, FaTags } from "react-icons/fa";
import { MdDelete } from "react-icons/md";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function CategoryPage() {
    const [categoryList, setCategoryList] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [categoryData, setCategoryData] = useState({ name: '', image: '', parentCategory: '' });
    const [parentCategories, setParentCategories] = useState([]);
    const [imageUploading, setImageUploading] = useState(false);
    const [savingCategory, setSavingCategory] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        handleCategoryList();
        fetchParentCategories();
    }, []);

    // Fetch all categories
    const handleCategoryList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/categorydata`);
            const categories = response.data || [];
            setCategoryList(categories);
            setFilteredCategories(categories);
        } catch (error) {
            alert("Failed to fetch categories. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch parent categories
    const fetchParentCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/parent-categories`);
            setParentCategories(response.data || []);
        } catch (error) {
            console.error("Failed to fetch parent categories:", error);
        }
    };

    // Search category with debouncing
    const handleSearchCategory = (event) => {
        const search = event.target.value.toLowerCase();
        setSearchTerm(search);
        
        if (search) {
            const filtered = categoryList.filter(category => 
                category.name.toLowerCase().includes(search)
            );
            setFilteredCategories(filtered);
        } else {
            setFilteredCategories(categoryList);
        }
    };

    // Handle image upload
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, WebP, or AVIF images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }

        try {
            setImageUploading(true);
            const formData = new FormData();
            formData.append('image', file);
            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData);
            setCategoryData({ ...categoryData, image: response.data.path });
            alert("Image uploaded successfully!");
        } catch (error) {
            alert("Image upload failed. Please try again.");
        } finally {
            setImageUploading(false);
        }
    };

    // Handle form input changes
    const handleChange = (event) => {
        setCategoryData({ ...categoryData, [event.target.name]: event.target.value });
    };

    // Validate input fields
    const validateFields = () => {
        if (!categoryData.name.trim()) {
            alert("Category name is required.");
            return false;
        }
        if (!categoryData.image.trim()) {
            alert("Category image is required.");
            return false;
        }
        return true;
    };

    // Add a new category
    const handleAddCategory = async () => {
        if (!validateFields()) return;

        try {
            setSavingCategory(true);
            const response = await axios.post(`${API_BASE_URL}/category`, categoryData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            handleCategoryList();
            setCategoryData({ name: '', image: '', parentCategory: '' });
            setShowAddModal(false);
        } catch (error) {
            alert("Failed to add category. Please try again.");
        } finally {
            setSavingCategory(false);
        }
    };

    // Delete a category
    const handleDeleteCategory = async (id) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;

        try {
            await axios.delete(`${API_BASE_URL}/delete-category/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Category deleted successfully!");
            handleCategoryList();
        } catch (error) {
            alert("Failed to delete category. Please try again.");
        }
    };

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">CATEGORY MANAGEMENT</h4>
                                <p className="text-muted p-0 m-0">Manage your product categories and organization</p>
                            </div>
                            <button 
                                type="button" 
                                className="btn theme-btn-blue-flat btn-sm rounded-0"
                                style={{
                                    padding: '6px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                                onClick={() => setShowAddModal(true)}
                            >
                                <FaPlus size={11} />
                                Add Category
                            </button>
                        </div>
                    </div>
                </div>

                {/* Control Panel — cloned from Brands page */}
                <div className="control-panel-bar">
                    <div className="control-panel-search">
                        <span
                            className="position-absolute top-50 translate-middle-y start-0 ps-3 text-muted"
                            style={{ pointerEvents: 'none' }}
                        >
                            <FaSearch style={{ fontSize: '14px' }} />
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm ps-5"
                            placeholder="Search by category name..."
                            style={{
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                height: '38px',
                                fontSize: '14px',
                                background: '#ffffff',
                                width: '100%'
                            }}
                            value={searchTerm}
                            onChange={handleSearchCategory}
                        />
                    </div>
                    <div className="control-panel-reset" style={{ marginLeft: 'auto' }}>
                        <small className="text-muted" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                            Showing {filteredCategories.length} of {categoryList.length} categories
                        </small>
                    </div>
                </div>

                {loading ? (
                    <AdminPageLoader message="Loading categories..." />
                ) : (
                    <div className="brands-grid-wrap">
                        {filteredCategories.length > 0 ? (
                            filteredCategories.map((data) => (
                                <div className="brands-grid-item" key={data._id}>
                                    <div className="brand-flat-card">
                                        <div className="brand-flat-img-wrap">
                                            <img
                                                src={`${API_BASE_URL}/${data.image}`}
                                                alt={data.name}
                                                className="brand-flat-img"
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/260x130?text=No+Image';
                                                }}
                                            />
                                        </div>
                                        <div className="brand-flat-body">
                                            <span className="brand-flat-name">{data.name}</span>
                                            <div className="brand-flat-actions">
                                                <Link
                                                    to={`/admin/categories/edit/${data._id}`}
                                                    className="brand-action-btn"
                                                    title="Edit Category"
                                                >
                                                    <FaRegEdit size={13} />
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteCategory(data._id)}
                                                    className="brand-action-btn brand-action-btn--danger"
                                                    title="Delete Category"
                                                >
                                                    <MdDelete size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ width: '100%' }}>
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center py-5">
                                        <FaFolder className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No categories found matching your search.' : 'No categories found'}
                                        </h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Add your first category to get started.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add Category Modal — assign-driver style overlay */}
            {showAddModal && (
                <div className="assign-modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="assign-modal-content" onClick={e => e.stopPropagation()}>

                        {/* Header */}
                        <div className="assign-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: '#e8eaf6', color: '#1a237e',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: '15px'
                                }}>
                                    <FaFolder />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                                        Add New Category
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>
                                        Fill in the details to add a new category
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowAddModal(false)}
                                style={{
                                    border: 'none', background: 'none', fontSize: '20px',
                                    color: '#94a3b8', cursor: 'pointer', padding: '0 2px',
                                    lineHeight: 1, transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Body */}
                        <div className="assign-modal-body">
                            <span style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', color: '#94a3b8', marginBottom: '12px', display: 'block' }}>
                                CATEGORY DETAILS
                            </span>
                            <div className="mb-3">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Category Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                    placeholder="Enter category name"
                                    name="name"
                                    value={categoryData.name}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="mb-3">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Parent Category</label>
                                <select 
                                    className="form-select" 
                                    style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                    name="parentCategory" 
                                    value={categoryData.parentCategory} 
                                    onChange={handleChange}
                                >
                                    <option value="">Select Parent Category</option>
                                    {parentCategories.map((parent) => (
                                        <option key={parent._id} value={parent.name}>
                                            {parent.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-1">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Category Image</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', padding: '7px 12px' }}
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                />
                                {imageUploading && (
                                    <div className="mt-2 d-flex align-items-center gap-1">
                                        <FaSpinner style={{ fontSize: '11px', color: '#94a3b8' }} />
                                        <small style={{ color: '#94a3b8', fontSize: '11px' }}>Uploading image...</small>
                                    </div>
                                )}
                                {categoryData.image && !imageUploading && (
                                    <div className="mt-2 d-flex align-items-center gap-1">
                                        <FaImage style={{ fontSize: '11px', color: '#16a34a' }} />
                                        <small style={{ color: '#16a34a', fontSize: '11px' }}>Image uploaded successfully</small>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="assign-modal-footer">
                            <button
                                type="button"
                                onClick={() => setShowAddModal(false)}
                                style={{
                                    flex: 1, padding: '8px 16px', borderRadius: '6px',
                                    border: '1px solid #e2e8f0', background: '#ffffff',
                                    color: '#374151', fontSize: '13px', fontWeight: '500',
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                                onMouseOver={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddCategory}
                                disabled={savingCategory || imageUploading}
                                style={{
                                    flex: 1, padding: '8px 16px', borderRadius: '6px',
                                    border: 'none', background: savingCategory || imageUploading ? '#c7d2fe' : '#1a237e',
                                    color: savingCategory || imageUploading ? '#6366f1' : '#ffff01',
                                    fontSize: '13px', fontWeight: '600',
                                    cursor: savingCategory || imageUploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '6px'
                                }}
                            >
                                {savingCategory ? (
                                    <><FaSpinner style={{ fontSize: '11px' }} /> Saving...</>
                                ) : 'Save Category'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default CategoryPage;
